import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase, Job } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function HomePage() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchWhat, setSearchWhat] = useState('');
  const [searchWhere, setSearchWhere] = useState('');

  useEffect(() => {
    fetchLatestJobs();
  }, []);

  const fetchLatestJobs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(4);

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchWhat) params.set('what', searchWhat);
    if (searchWhere) params.set('where', searchWhere);
    window.REACT_APP_NAVIGATE(`/jobs?${params.toString()}`);
  };

  const handleKeywordClick = (keyword: string) => {
    const params = new URLSearchParams();
    params.set('what', keyword);
    window.REACT_APP_NAVIGATE(`/jobs?${params.toString()}`);
  };

  const formatSalary = (salaryRange: string) => {
    if (!salaryRange) return 'Competitive';
    return salaryRange;
  };

  const getTimeAgo = (createdAt: string) => {
    const now = new Date();
    const posted = new Date(createdAt);
    const diffMs = now.getTime() - posted.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const trendingKeywords = ['Remote', 'Python', 'Marketing', 'Senior Engineer', 'Design', 'Sales'];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Global Navigation */}
      <Navbar />

      {/* Hero Section */}
      <div className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Hero Section */}
        <section className="pt-32 pb-24 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Find your next <span className="gradient-text">dream job</span>
            </h1>
            <p className="text-xl text-[#a0a0a0] mb-12 max-w-2xl mx-auto">
              Discover thousands of opportunities from top companies. Your perfect role is waiting.
            </p>

            {/* Search Bar */}
            <div className="bg-[#1c1c1c] rounded-lg p-2 shadow-xl border border-[#333333] max-w-3xl mx-auto">
              <div className="flex flex-col md:flex-row gap-2">
                <div className="flex-1 flex items-center px-4 py-3 bg-[#2d2d2d] rounded border border-[#333333] focus-within:border-[#6366f1] transition-colors">
                  <i className="ri-search-line text-[#a0a0a0] text-xl mr-3"></i>
                  <input
                    type="text"
                    placeholder="Job title, keywords, or company"
                    value={searchWhat}
                    onChange={(e) => setSearchWhat(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="flex-1 bg-transparent text-white placeholder-[#a0a0a0] outline-none"
                  />
                </div>
                <div className="flex-1 flex items-center px-4 py-3 bg-[#2d2d2d] rounded border border-[#333333] focus-within:border-[#6366f1] transition-colors">
                  <i className="ri-map-pin-line text-[#a0a0a0] text-xl mr-3"></i>
                  <input
                    type="text"
                    placeholder="City, state, or zip code"
                    value={searchWhere}
                    onChange={(e) => setSearchWhere(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="flex-1 bg-transparent text-white placeholder-[#a0a0a0] outline-none"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="px-8 py-3 bg-[#6366f1] text-white font-semibold rounded hover:bg-[#5558e3] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Find Jobs
                </button>
              </div>
            </div>

            {/* Trending Keywords */}
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <span className="text-[#a0a0a0] text-sm">Trending:</span>
              {trendingKeywords.map((keyword) => (
                <button
                  key={keyword}
                  onClick={() => handleKeywordClick(keyword)}
                  className="px-4 py-2 bg-[#1c1c1c] text-white text-sm rounded-full border border-[#333333] hover:border-[#6366f1] hover:bg-[#2d2d2d] transition-all cursor-pointer"
                >
                  {keyword}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Counter */}
        <section className="py-16 bg-[#141414] border-y border-[#333333]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-white mb-2">10,000+</div>
                <div className="text-[#a0a0a0]">Active Jobs</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-white mb-2">500+</div>
                <div className="text-[#a0a0a0]">Verified Companies</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-white mb-2">24h</div>
                <div className="text-[#a0a0a0]">Average Response Time</div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Employers */}
        <section className="py-24 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Top Companies Hiring Now
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {['TechCorp', 'Innovate', 'GlobalFlow', 'NexGen', 'DataStream', 'CloudNine'].map((company) => (
                <div
                  key={company}
                  className="bg-[#1c1c1c] border border-[#333333] rounded-lg p-8 flex items-center justify-center hover:border-[#6366f1] transition-all card-hover cursor-pointer"
                >
                  <span className="text-white font-semibold text-lg">{company}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Zing */}
        <section className="py-24 px-4 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-4">
              Why Choose Zing?
            </h2>
            <p className="text-[#a0a0a0] text-center mb-16 max-w-2xl mx-auto">
              We're revolutionizing the job search experience with cutting-edge technology and genuine care for your career.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-[#1c1c1c] border border-[#333333] rounded-lg p-8 card-hover">
                <div className="w-16 h-16 bg-gradient-to-br from-[#6366f1] to-[#8b5cf6] rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-rocket-line text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Fast Application</h3>
                <p className="text-[#a0a0a0] leading-relaxed">
                  One-click apply to thousands of jobs. No more repetitive form filling. Your profile, instantly shared.
                </p>
              </div>
              <div className="bg-[#1c1c1c] border border-[#333333] rounded-lg p-8 card-hover">
                <div className="w-16 h-16 bg-gradient-to-br from-[#6366f1] to-[#8b5cf6] rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-shield-check-line text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Verified Listings</h3>
                <p className="text-[#a0a0a0] leading-relaxed">
                  No scams, just real opportunities. Every company is verified. Every job is legitimate. Your trust, protected.
                </p>
              </div>
              <div className="bg-[#1c1c1c] border border-[#333333] rounded-lg p-8 card-hover">
                <div className="w-16 h-16 bg-gradient-to-br from-[#6366f1] to-[#8b5cf6] rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-line-chart-line text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Salary Transparent</h3>
                <p className="text-[#a0a0a0] leading-relaxed">
                  Know what you'll earn before you apply. No surprises. No wasted time. Just honest, upfront compensation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Latest Jobs Preview */}
        <section className="py-24 px-4 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-12">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Freshly Posted Jobs</h2>
                <p className="text-[#a0a0a0]">Hot opportunities just added to our platform</p>
              </div>
              <Link
                to="/jobs"
                className="px-6 py-3 bg-[#6366f1] text-white font-semibold rounded hover:bg-[#5558e3] transition-colors whitespace-nowrap cursor-pointer hidden md:block"
              >
                View All Jobs
              </Link>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-[#1c1c1c] border border-[#333333] rounded-lg p-6 animate-shimmer h-48"></div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {jobs.map((job) => (
                  <Link
                    key={job.id}
                    to={`/job/${job.id}`}
                    className="bg-[#1c1c1c] border border-[#333333] rounded-lg p-6 card-hover cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        {job.company_logo ? (
                          <img
                            src={job.company_logo}
                            alt={job.company}
                            className="w-12 h-12 rounded object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={`w-12 h-12 bg-[#2d2d2d] border-2 border-[#6366f1] rounded flex items-center justify-center ${job.company_logo ? 'hidden' : ''}`}>
                          <i className="ri-building-line text-[#6366f1] text-xl"></i>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-[#6366f1] hover:text-[#5558e3] transition-colors">
                            {job.title}
                          </h3>
                          <p className="text-[#a0a0a0] text-sm">{job.company}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1 bg-[#2d2d2d] text-[#a0a0a0] text-xs rounded-full border border-[#333333]">
                        <i className="ri-map-pin-line mr-1"></i>
                        {job.location}
                      </span>
                      {job.job_type && (
                        <span className="px-3 py-1 bg-[#2d2d2d] text-[#a0a0a0] text-xs rounded-full border border-[#333333]">
                          {job.job_type}
                        </span>
                      )}
                      {job.experience_level && (
                        <span className="px-3 py-1 bg-[#2d2d2d] text-[#a0a0a0] text-xs rounded-full border border-[#333333]">
                          {job.experience_level}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-white font-bold">{formatSalary(job.salary_range)}</span>
                      <span className="text-[#a0a0a0] text-sm">{getTimeAgo(job.created_at)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <div className="text-center mt-12 md:hidden">
              <Link
                to="/jobs"
                className="inline-block px-8 py-3 bg-[#6366f1] text-white font-semibold rounded hover:bg-[#5558e3] transition-colors whitespace-nowrap cursor-pointer"
              >
                View All Jobs
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
